import { useSelector } from 'react-redux';

import { RootState } from '../redux/rootReducer';
import { CardTemplateDb } from '../redux/cardTemplateDbSlice';
import { CardTemplateAttributeDb, isImageAttributeTemplate } from '../redux/cardTemplateAttributeDbSlice';
import { CardTemplateShapeDb, isCardTemplateImageShape } from '../redux/cardTemplateShapeDbSlice';
import { ImageDb } from '../redux/imageDbSlice';
import { BookDb } from '../redux/bookDbSlice';
import { SheetDb } from '../redux/sheetDbSlice';
import { SheetTreeDb } from '../redux/sheetTreeDbSlice';
import { SheetInformationDb } from '../redux/sheetInformationDbSlice';
import { CardDb } from '../redux/cardDbSlice';
import { CardAttributeDb, isImageAttribute } from '../redux/cardAttributeDbSlice';
import { CardShapeDb, isImageShape } from '../redux/cardShapeDbSlice';
import { MemoCardContentDb } from '../redux/memoCardContentDbSlice';
import { DrawCardContentDb } from '../redux/drawCardContentDbSlice';
import { UiCardItemDb, isStaticImageUiSketchItem } from '../redux/uiCardItemDbSlice';
import { ArrowDb } from '../redux/arrowDbSlice';
import { GoodsKindType } from '../redux/shopSlice';
import { Hash } from '../general';

export type PublishDataType = {
  defaultName: string;
  cardTemplates: Hash<CardTemplateDb>;
  cardTemplateAttributes: Hash<CardTemplateAttributeDb>;
  cardTemplateShapes: Hash<CardTemplateShapeDb>;
  images: Hash<ImageDb>;
} | {
  defaultName: string;
  books: Hash<BookDb>;
  sheets: Hash<SheetDb>;
  sheetTrees: Hash<SheetTreeDb>;
  sheetInformations: Hash<SheetInformationDb>;
  cards: Hash<CardDb>;
  cardAttributes: Hash<CardAttributeDb>;
  cardShapes: Hash<CardShapeDb>;
  memoCardContents: Hash<MemoCardContentDb>;
  drawCardContents: Hash<DrawCardContentDb>;
  // uiCardItems: Hash<UiCardItemDb>;
  arrows: Hash<ArrowDb>;
  images: Hash<ImageDb>;
}
const useMakePublishData = () => {
  const cardTemplateDb = useSelector((state: RootState) => state.cardTemplateDb);
  const cardTemplateAttributeDb = useSelector((state: RootState) => state.cardTemplateAttributeDb);
  const cardTemplateShapeDb = useSelector((state: RootState) => state.cardTemplateShapeDb);
  const imageDb = useSelector((state: RootState) => state.imageDb);
  const bookDb = useSelector((state: RootState) => state.bookDb);
  const sheetDb = useSelector((state: RootState) => state.sheetDb);
  const sheetTreeDb = useSelector((state: RootState) => state.sheetTreeDb);
  const sheetInformationDb = useSelector((state: RootState) => state.sheetInformationDb);
  const cardDb = useSelector((state: RootState) => state.cardDb);
  const cardAttributeDb = useSelector((state: RootState) => state.cardAttributeDb);
  const cardShapeDb = useSelector((state: RootState) => state.cardShapeDb);
  const memoCardContentDb = useSelector((state: RootState) => state.memoCardContentDb);
  const drawCardContentDb = useSelector((state: RootState) => state.drawCardContentDb);
  // const uiCardItemDb = useSelector((state: RootState) => state.uiCardItemDb);
  const arrowDb = useSelector((state: RootState) => state.arrowDb);

  const makePublishData = (goodsKind: GoodsKindType, id?: string, paletteData?: { name: string; toPublishIds: string[] }): PublishDataType | undefined => {
    if (goodsKind === 'card_template' && id) {
      const cardTemplates: Hash<CardTemplateDb> = { [id]: cardTemplateDb.cardTemplates[id] };
      const defaultName = cardTemplateDb.cardTemplates[id].name;
      const attributeIds = Object.keys(cardTemplateAttributeDb.cardTemplateAttributes).filter(targetId => cardTemplateAttributeDb.cardTemplateAttributes[targetId].cardTemplateId === id);
      const cardTemplateAttributes: Hash<CardTemplateAttributeDb> = {};
      attributeIds.forEach(targetId => cardTemplateAttributes[targetId] = cardTemplateAttributeDb.cardTemplateAttributes[targetId])
      const shapeIds = Object.keys(cardTemplateShapeDb.cardTemplateShapes).filter(targetId => cardTemplateShapeDb.cardTemplateShapes[targetId].cardTemplateId === id);
      const cardTemplateShapes: Hash<CardTemplateShapeDb> = {};
      shapeIds.forEach(targetId => cardTemplateShapes[targetId] = cardTemplateShapeDb.cardTemplateShapes[targetId])

      const imageIds: string[] = [];
      attributeIds.forEach(targetId => {
        const value = cardTemplateAttributes[targetId];
        if (isImageAttributeTemplate(value)) imageIds.push(value.defaultImage.imageId);
      })
      shapeIds.forEach(targetId => {
        const value = cardTemplateShapes[targetId];
        if (isCardTemplateImageShape(value)) imageIds.push(value.image.imageId);
      })
      const uniqueImageIds = Array.from(new Set(imageIds)).filter(id => id !== '');
      const images: Hash<ImageDb> = {};
      uniqueImageIds.forEach(targetId => images[targetId] = imageDb.images[targetId])

      return { defaultName, cardTemplates, cardTemplateAttributes, cardTemplateShapes, images };
    } else if (goodsKind === 'book' && id) {
      const books: Hash<BookDb> = { [id]: bookDb.books[id] };
      const defaultName = bookDb.books[id].name;

      const sheetTreeIds = Object.keys(sheetTreeDb.sheetTrees)
        .filter(targetId => sheetTreeDb.sheetTrees[targetId].bookId === id);
      const sheetTrees: Hash<SheetTreeDb> = {};
      sheetTreeIds.forEach(targetId => sheetTrees[targetId] = sheetTreeDb.sheetTrees[targetId])

      const sheetIds = sheetTreeIds.map(targetId => sheetTrees[targetId].sheetId);
      const sheets: Hash<SheetDb> = {};
      sheetIds.forEach(targetId => sheets[targetId] = sheetDb.sheets[targetId])

      const sheetInformationIds = Object.keys(sheetInformationDb.sheetInformations)
        .filter(targetId => sheetIds.includes(sheetInformationDb.sheetInformations[targetId].sheetId));
      const sheetInformations: Hash<SheetInformationDb> = {};
      sheetInformationIds.forEach(targetId => sheetInformations[targetId] = sheetInformationDb.sheetInformations[targetId])

      const cardIds = Object.keys(cardDb.cards).filter(targetId => sheetIds.includes(cardDb.cards[targetId].sheetId));
      const cards: Hash<CardDb> = {};
      cardIds.forEach(targetId => cards[targetId] = cardDb.cards[targetId])

      const cardAttributeIds = Object.keys(cardAttributeDb.cardAttributes)
        .filter(targetId => cardIds.includes(cardAttributeDb.cardAttributes[targetId].cardId));
      const cardAttributes: Hash<CardAttributeDb> = {};
      cardAttributeIds.forEach(targetId => cardAttributes[targetId] = cardAttributeDb.cardAttributes[targetId])

      const cardShapeIds = Object.keys(cardShapeDb.cardShapes)
        .filter(targetId => cardIds.includes(cardShapeDb.cardShapes[targetId].cardId));
      const cardShapes: Hash<CardShapeDb> = {};
      cardShapeIds.forEach(targetId => cardShapes[targetId] = cardShapeDb.cardShapes[targetId])

      const memoCardContentIds = Object.keys(memoCardContentDb.memoCardContents)
        .filter(targetId => cardIds.includes(memoCardContentDb.memoCardContents[targetId].cardId));
      const memoCardContents: Hash<MemoCardContentDb> = {};
      memoCardContentIds.forEach(targetId => memoCardContents[targetId] = memoCardContentDb.memoCardContents[targetId])

      const drawCardContentIds = Object.keys(drawCardContentDb.drawCardContents)
        .filter(targetId => cardIds.includes(drawCardContentDb.drawCardContents[targetId].cardId));
      const drawCardContents: Hash<DrawCardContentDb> = {};
      drawCardContentIds.forEach(targetId => drawCardContents[targetId] = drawCardContentDb.drawCardContents[targetId])

      const arrowIds = Object.keys(arrowDb.arrows).filter(targetId => sheetIds.includes(arrowDb.arrows[targetId].sheetId));
      const arrows: Hash<ArrowDb> = {};
      arrowIds.forEach(targetId => arrows[targetId] = arrowDb.arrows[targetId])

      const imageIds: string[] = [];
      const book = books[id];
      if (book.coverImage) imageIds.push(book.coverImage.imageId);
      cardAttributeIds.forEach(targetId => {
        const value = cardAttributes[targetId];
        if (isImageAttribute(value)) {
          imageIds.push(value.defaultImage.imageId);
          if (value.content.imageId !== '' && value.content.imageId !== value.defaultImage.imageId) imageIds.push(value.content.imageId);
        }
      })
      cardShapeIds.forEach(targetId => {
        const value = cardShapes[targetId];
        if (isImageShape(value)) imageIds.push(value.image.imageId);
      })
      arrowIds.forEach(targetId => {
        const value = arrows[targetId];
        imageIds.push(value.imageId);
      })
      const uniqueImageIds = Array.from(new Set(imageIds)).filter(id => id !== '');
      const images: Hash<ImageDb> = {};
      uniqueImageIds.forEach(targetId => images[targetId] = imageDb.images[targetId])

      return { defaultName, books, sheets, sheetTrees, sheetInformations,
        cards, cardAttributes, cardShapes, memoCardContents, drawCardContents,
        arrows, images
      };
    } else if (goodsKind === 'palette' && paletteData) {
      const defaultName = paletteData.name;
      const cardTemplates: Hash<CardTemplateDb> = {};
      const cardTemplateAttributes: Hash<CardTemplateAttributeDb> = {};
      const cardTemplateShapes: Hash<CardTemplateShapeDb> = {};
      const images: Hash<ImageDb> = {};

      paletteData.toPublishIds.forEach(cardTemplateId => {
        const data = makePublishData('card_template', cardTemplateId);
        if (!data || !('cardTemplates' in data)) return;
        Object.keys(data.cardTemplates).forEach(targetId => cardTemplates[targetId] = data.cardTemplates[targetId])
        Object.keys(data.cardTemplateAttributes).forEach(targetId => cardTemplateAttributes[targetId] = data.cardTemplateAttributes[targetId])
        Object.keys(data.cardTemplateShapes).forEach(targetId => cardTemplateShapes[targetId] = data.cardTemplateShapes[targetId])
        Object.keys(data.images).forEach(targetId => {
          if (!images[targetId]) images[targetId] = data.images[targetId];
        })
      })
      return { defaultName, cardTemplates, cardTemplateAttributes, cardTemplateShapes, images };
    }
  }
  return makePublishData;
}

export default useMakePublishData;
